import React from "react";
import { Text } from "../../atoms/text/text";
import styles from "./channel-search.module.css";
import pillStyles from "./show-more-pill.module.css";
import type { ChannelSearchItem } from "./channel-search.shared";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * SR7 + AX11 — one removable chip per selection, and the Show More control
 * once they pass the row limit.
 *
 * ⛔ The remove button is keyed and reported by `item.id`. A label-keyed
 * remove is the WAT-11767 defect: two channels sharing a name collapse into
 * one chip, and removing either removes the first.
 *
 * ⚠ NOT `atoms/tag` — that file says so itself: "NOT A CHIP. A chip is a
 * control the user picks, so it is focusable and announces a pressed state. A
 * Tag is read, never operated."
 *
 * ⚠ NOT the organism's `Disclosure`/`ShowMoreToggle` either. Those are native
 * `<details>`/`<summary>`, which hides ALL non-summary content when closed —
 * the chips have to stay visible and only CLAMP. A molecule also cannot import
 * upward from an organism. Reported.
 *
 * ⛔ NO `data-linkposition` / `data-linkdestination` ON THE REMOVE BUTTON. GTM's
 * Link Click trigger names a control from its `aria-label`, and that name now
 * carries AX11's position suffix — so letting the container fire would report
 * every removal under a name the control arm has never sent. The removal
 * reaches the caller as a `remove-chip` change instead, and the caller names it.
 */
export interface ChipRowProps {
  id: string;
  /** The selected items, in the caller's `selectedIds` order. */
  items: ChannelSearchItem[];
  /** Content-managed verb; the chip's own text completes the accessible name. */
  removeLabel: string;
  moreLabel: string;
  lessLabel: string;
  expanded: boolean;
  /** Whether the chips exceed the row limit, so the control is needed at all. */
  overflowing: boolean;
  /**
   * Index of the first chip the collapsed clamp cuts off, or null while they
   * all fit. ⚠ Measured, so it is null until the first measurement lands —
   * which is the safe way round: an unmeasured chip stays operable.
   */
  clippedFrom: number | null;
  rowLimitDesktop: number;
  rowLimitMobile: number;
  listRef: React.RefObject<HTMLUListElement>;
  toggleRef: React.RefObject<HTMLButtonElement>;
  /** Internal to this folder; `index` is what AX11's focus move needs, and the
   *  whole item is what lets the caller name the removal. */
  onRemove: (item: ChannelSearchItem, index: number) => void;
  onToggleExpanded: () => void;
}

/**
 * AX11 — "each chip's remove button, with the channel's name and number", and
 * "two entries with the same name and number are told apart by position".
 *
 * ⛔ THE POSITION IS UNCONDITIONAL. Adding it only where a duplicate exists
 * makes a chip's accessible name change when an UNRELATED sibling is removed,
 * which is harder to live with than the ambiguity it closes. `secondary` is
 * already in the name and is the FIRST discriminator; it is simply not enough on
 * its own, because the live feed carries "Sports Mix" twice on 205 (WAT-11771)
 * and `role="button"` supports no `aria-posinset` to carry the position instead.
 *
 * ⚠ AND IT IS NOW THE ONLY COPY ON THE BUTTON. WAT-12339's audit heard
 * "ESPN 206, 1 of 9 … Remove ESPN 206, 1 of 9" arrowing the row: the chip
 * published its position three ways and its name twice. The per-chip
 * `aria-posinset`/`aria-setsize` went, the list itself still publishes the
 * position, and this suffix stays — WAT-12418 asserts it, and it is what
 * tells two same-named chips apart on the button.
 */
export function chipRemoveName(
  removeLabel: string,
  item: ChannelSearchItem,
  index: number,
  total: number
): string {
  const named = item.secondary ? `${item.label} ${item.secondary}` : item.label;
  return `${removeLabel} ${named}, ${index + 1} of ${total}`;
}

/**
 * SC 2.4.11, the other half — the toggle is the ONLY route to the chips the
 * collapsed clamp has taken out of the tree, so its name has to carry how many
 * went. Without it the row reports three items, the first says "1 of 9", and
 * nothing accounts for the other six.
 *
 * ⚠ The authored label stays at the FRONT: WCAG 2.5.3 Label in Name asks that
 * the accessible name start with the visible wording, so the count cannot lead.
 *
 * ⚠ "selections", not "channels" — this molecule does not know what a channel
 * is (`channel-search.shared.tsx`'s item contract). Hard-coded, and singular at
 * one, for the same reason `channelSearchStatus` hard-codes "1 suggestion
 * available.": a screen reader is the only thing that ever hears it.
 */
export function chipToggleName(moreLabel: string, hidden: number): string {
  return hidden === 1 ?
    `${moreLabel}, 1 more selection` :
    `${moreLabel}, ${hidden} more selections`;
}

function ChipRowImpl({
  id,
  items,
  removeLabel,
  moreLabel,
  lessLabel,
  expanded,
  overflowing,
  clippedFrom,
  rowLimitDesktop,
  rowLimitMobile,
  listRef,
  toggleRef,
  onRemove,
  onToggleExpanded
}: ChipRowProps) {
  /* How many chips the clamp is holding back right now: none while the row is
     expanded, none before the first measurement lands, and none if a stale
     index outruns a list that has since got shorter. */
  const hidden =
    !expanded && clippedFrom !== null ? Math.max(items.length - clippedFrom, 0) : 0;

  return (
    <>
      <ul
        className={[styles.chips, expanded ? styles.chipsExpanded : null]
          .filter(Boolean)
          .join(" ")}
        id={id}
        ref={listRef}
        /* ⚠ RESTATED, not redundant. WAT-12339 dropped the per-chip
           `aria-posinset`/`aria-setsize`, so this list is the only thing left
           publishing a chip's position — and `.chips` is styled
           `list-style: none` (`channel-search.module.css:141`), which WebKit
           is reported to take list semantics away for. Same reason
           `ribbon-track.client.tsx:194` and `parallax-marquee.tsx:985` restate
           it on their own lists; one attribute here replaces two per chip. */
        role="list"
        /* The two limits travel as custom properties because the stylesheet's
           media query picks between them and a query cannot read a prop. */
        style={
          {
            "--channel-search-chip-rows-desktop": rowLimitDesktop,
            "--channel-search-chip-rows-mobile": rowLimitMobile
          } as React.CSSProperties
        }
      >
        {items.map((item, index) => {
          /* ⚠ WCAG 2.2 SC 2.4.11 Focus Not Obscured. The collapsed row clamps
             with `overflow: hidden` (`channel-search.module.css:142`), so a
             chip past the cut is drawn nowhere — it must not take a tab stop,
             or Tab lands on something invisible. Show More is the way to it,
             the same bargain a collapsed disclosure makes.
             ⚠ The `aria-hidden` half is a JUDGEMENT, not the SC: 2.4.11 is
             about focus, which `tabIndex` alone closes. It goes on because a
             reader reaching a clipped chip by virtual cursor and activating it
             would move focus behind the cut anyway — at the price of a hidden
             chip being absent from the tree rather than merely unreachable.
             ⚠ Gated on `expanded` as well as the index: expanding lifts the
             clamp, and the measurement still reports the row the chips sit
             on. */
          const clipped = !expanded && clippedFrom !== null && index >= clippedFrom;
          return (
          /* ⛔ Keyed on the opaque id. Two selections sharing a label and a
             number are two chips. */
            <li aria-hidden={clipped || undefined} className={styles.chip} key={item.id}>
              {/* `Desktop/Body/Body 2`, 16px — the chip's own label step
                  (`15565:22110`), not the 14px the suggestion rows use. */}
              <Text as="span" variant="body-2">
                {item.secondary ? `${item.label} ${item.secondary}` : item.label}
              </Text>
              <button
              /* AX11 — the visible chip text is repeated into the name because
                 the button is icon-only, and the position is what tells two
                 identical chips apart. See `chipRemoveName`. ⚠ The position
                 still counts EVERY selection, clipped or not: "3 of 9" is what
                 the reader has chosen, not what is currently on screen — and
                 the toggle below now names how many of the nine the clamp is
                 holding, so the six that are absent are accounted for. */
                aria-label={chipRemoveName(removeLabel, item, index, items.length)}
                className={styles.chipRemove}
                onClick={() => onRemove(item, index)}
                /* -1 rather than omitted: the chip is inside `aria-hidden`, and
                   a TABBABLE element in there is an a11y defect of its own. */
                tabIndex={clipped ? -1 : undefined}
                type="button"
              >
                <svg
                  aria-hidden="true"
                  className={styles.chipRemoveGlyph}
                  focusable="false"
                  viewBox="0 0 12 12"
                >
                  <path
                    d="M2 2 10 10M10 2 2 10"
                    fill="none"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeWidth="1.75"
                  />
                </svg>
              </button>
            </li>
          );
        })}
      </ul>
      {overflowing ?
        /* The tonal pill between two rules (`16557:25133`). A native button rather than
           `atoms/button`, which carries no ref, `aria-expanded` or `aria-controls`. */
        <div className={pillStyles.pillRow}>
          <button
            aria-controls={id}
            aria-expanded={expanded}
            /* ⚠ NAME ONLY, and only while chips are hidden. The visible wording
               stays the authored label, and expanded the control names itself.
               ⛔ Still no `data-linkposition` / `data-linkdestination` here —
               GTM reads a control's name off `aria-label`, which is the same
               reason the header gives for keeping them off the remove button. */
            aria-label={hidden > 0 ? chipToggleName(moreLabel, hidden) : undefined}
            className={`${pillStyles.pill} ${styles.showMore}`}
            onClick={onToggleExpanded}
            ref={toggleRef}
            type="button"
          >
            <Text as="span" className={styles.showMoreLabel} variant="body-3" weight="medium">
              {expanded ? lessLabel : moreLabel}
            </Text>
            <svg aria-hidden="true" className={styles.showMoreChevron} focusable="false" viewBox="0 0 16 16">
              <path
                d="M4 6.5 8 10.5 12 6.5"
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.75"
              />
            </svg>
          </button>
        </div> :
        null}
    </>
  );
}
export const ChipRow = withSafeRender(ChipRowImpl, "ChipRow");

export default ChipRow;
